import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPropertyType } from "reduxStore/action/masterActions";
import {
  getAllProperty,
  getPropertyFilterList,
  propertyGreMobileOtpUpdate,
  propertyVisitorOtpSmsConfigGet,
  propertyVisitorOtpSmsConfigUpdate,
  propertyVisitorOtpSmsConfigClear,
  propertyGreBannerUpdate,
  propertyGreBannerRemove,
  getAgentProperty,
} from "reduxStore/action/propertyActions";
import TableDataNotFound from "component/TableDataNotFound";
import TableListSkeleton from "component/common/TableListSkeleton";
import strings from "translate/language";
import { handleListResponse, seo, normalizeMongoId } from "utils/utils";
import { AdminRoleId, IS_PRODUCTION_ENVIRONMENT, IS_STAGING_ENVIRONMENT } from "utils/constants";
import moment from "moment";
import Breadcrumb from "component/common/breadcrumb";
import usePermission from "utils/usePermission";
import Pagination from "component/common/Pagination";
import usePage from "utils/usePage";
import { getReportPropertyList } from "reduxStore/action/reportActions";
import Select from "react-select";
// import { syncAllProjects } from "../../reduxStore/action/propertyActions";
import { handleDetailResponse } from "utils/utils";
import { errorToast } from "utils/httpClient";
import propertyService from "reduxStore/service/propertyService";
import {
  GRE_BANNER_ALLOWED_MIME_TYPES,
  validateGreBannerFile,
} from "utils/greBannerUpload";
import { formatAppointmentCellValue } from "utils/appointmentListStatusDisplay";
import "./PropertyListing.css";

const renderPropertyCell = (value, toneClass = "") => {
  const text = formatAppointmentCellValue(value);
  const tone = text === "-" ? "property-listing-table__empty" : toneClass;
  return (
    <span className={`property-listing-table__cell ${tone}`.trim()}>{text}</span>
  );
};

const Property = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = usePage();
  const [formVaild, setFormVaild] = useState({});
  const [endDateDisable, setEndDateDisable] = useState(true);
  const [apiResponse, setApiResponse] = useState([]);
  const [isListLoading, setIsListLoading] = useState(true);
  const [propertyList, setPropertyList] = useState([]);
  const propertySelector = useSelector((state) => state.propertyData) || {};
  const ReportListSelector = useSelector((state) => state.report);
  const { create, view, edit, isAdmin } = usePermission({
    create: "add_property",
    view: "view_property",
    edit: "edit_property",
  });

  const user_id = useSelector((state) => {
    const { response } = state.login;
    return handleDetailResponse(response.data)._id;
  });

  const role_id = useSelector((state) => {
    const { response } = state.login;
    return handleDetailResponse(response.data).role_id;
  });

  const isSuperAdmin = String(role_id || "") === String(AdminRoleId);

  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    location: "",
    property_name: "",
    property_type: "",
    gre_mobile_otp: "",
  });

  const [searchData, setSearchData] = useState([]);
  const [propertyMediaBaseUrl, setPropertyMediaBaseUrl] = useState("");
  // Modal preview state for super admin GRE banner column.
  const [bannerPreview, setBannerPreview] = useState({
    open: false,
    url: "",
    title: "",
    objectUrl: "",
  });
  const [bannerRemoveConfirm, setBannerRemoveConfirm] = useState({
    open: false,
    propertyId: null,
    propertyTitle: "",
    removing: false,
  });
  const [bannerUploadModal, setBannerUploadModal] = useState({
    open: false,
    propertyId: null,
    propertyTitle: "",
    hasExistingBanner: false,
    selectedFile: null,
    previewUrl: "",
    error: "",
    uploading: false,
  });
  const [otpSmsConfigModal, setOtpSmsConfigModal] = useState({
    open: false,
    propertyId: null,
    propertyTitle: "",
    otpEnabled: false,
    loading: false,
    saving: false,
    clearing: false,
    form: {
      auth_key: "",
      sender_id: "",
      api_url: "",
      route: "",
      template_id: "",
      otp_message: "",
      default_site_name: "",
      sms_mode: "off",
    },
    meta: {
      configured: false,
      has_auth_key: false,
      auth_key_hint: "",
      encryption_configured: true,
      is_production_backend: false,
      is_staging_backend: false,
    },
    error: "",
  });
  const [otpSmsMissingCredsConfirm, setOtpSmsMissingCredsConfirm] = useState({
    open: false,
    modeLabel: "",
  });
  const [otpSmsProdDevModeConfirm, setOtpSmsProdDevModeConfirm] = useState({
    open: false,
    modeLabel: "",
  });
  const [otpSmsStagingLiveConfirm, setOtpSmsStagingLiveConfirm] = useState({
    open: false,
  });
  const greBannerFileInputRef = useRef(null);

  const isProdSmsEnvironment =
    IS_PRODUCTION_ENVIRONMENT ||
    Boolean(otpSmsConfigModal.meta?.is_production_backend);
  const isStagingSmsEnvironment =
    !isProdSmsEnvironment &&
    (IS_STAGING_ENVIRONMENT ||
      Boolean(otpSmsConfigModal.meta?.is_staging_backend));

  const closeBannerPreview = () => {
    setBannerPreview((current) => {
      if (current.objectUrl) {
        URL.revokeObjectURL(current.objectUrl);
      }
      return { open: false, url: "", title: "", objectUrl: "" };
    });
  };

  // Opens preview modal by loading banner blob from getGreBannerImage API.
  const handleGreBannerPreview = async (item) => {
    const propertyId = normalizeMongoId(item?._id ?? item?.property_id);
    if (!propertyId) {
      return;
    }

    try {
      const response = await propertyService.getGreBannerImageBlob({
        property_id: propertyId,
      });
      if (response?.noBanner || !response?.data) {
        errorToast(strings.greBannerUpdateFailedTitle);
        return;
      }
      const objectUrl = URL.createObjectURL(response.data);
      setBannerPreview((current) => {
        if (current.objectUrl) {
          URL.revokeObjectURL(current.objectUrl);
        }
        return {
          open: true,
          url: objectUrl,
          title: item.property_title,
          objectUrl,
        };
      });
    } catch (error) {
      errorToast(strings.greBannerUpdateFailedTitle);
    }
  };

  const { response } = propertySelector;

  const propertyTypesSelector = useSelector((state) => state.propertyTypes);
  const navigatpage = async (navname) => {
    navigate(navname);
  };

  useEffect(() => {
    if (isAdmin) {
      const { reportPropertyList } = ReportListSelector;
      if (reportPropertyList) {
        if (reportPropertyList?.status === 200) {
          setPropertyList(handleListResponse(reportPropertyList.data));
        } else {
          setPropertyList([]);
        }
      }
    }
  }, [ReportListSelector]);

  useEffect(() => {
    if (propertyTypesSelector?.response) {
      const { response } = propertyTypesSelector;
      if (response?.status === 200) {
        setSearchData(response.data);
      } else {
        setSearchData([]);
      }
    }
  }, [propertyTypesSelector]);

  useEffect(() => {
    dispatch(getPropertyType());
    setIsListLoading(true);
    if (isAdmin) {
      dispatch(getAllProperty({ offset: page, limit: 10 }));
    } else {
      dispatch(getAgentProperty({ offset: page, limit: 10 }));
    }
  }, [dispatch]);

  const buildListParams = (offset = page) => ({
    limit: 10,
    offset,
    start_date: form.start_date,
    end_date: form.end_date,
    location: form.location,
    property_name: form?.site?.property_title,
    property_type: form.property_type,
    gre_mobile_otp: form.gre_mobile_otp,
  });

  const refreshPropertyList = (offset = page) => {
    setIsListLoading(true);
    const params = buildListParams(offset);
    if (isAdmin) {
      dispatch(getAllProperty(params));
    } else {
      dispatch(getAgentProperty(params));
    }
  };

  const matchesPropertyId = (item, propertyId) =>
    String(item?._id || "") === String(propertyId || "") ||
    String(item?.property_id || "") === String(propertyId || "");

  const applyClientFilters = (list) => {
    if (!Array.isArray(list)) {
      return [];
    }

    let result = list;

    if (form.property_type === "1") {
      result = result.filter((item) => item.status === true);
    } else if (form.property_type === "2") {
      result = result.filter((item) => item.status === false);
    }

    if (form.gre_mobile_otp === "1") {
      result = result.filter((item) => Boolean(item.gre_mobile_otp_enabled));
    } else if (form.gre_mobile_otp === "2") {
      result = result.filter((item) => !item.gre_mobile_otp_enabled);
    }

    return result.sort((a, b) => {
      if (!!a.status !== !!b.status) {
        return a.status ? -1 : 1;
      }
      return new Date(a.createdDate) < new Date(b.createdDate) ? 1 : -1;
    });
  };

  useEffect(() => {
    if (propertySelector.create) {
      return;
    }

    if (response && response.status === 200) {
      const list = handleListResponse(response?.data);
      setApiResponse(applyClientFilters(list));
      setPropertyList(list);
      if (response?.property_media_base_url) {
        setPropertyMediaBaseUrl(response.property_media_base_url);
      }
      setIsListLoading(false);
    } else if (response) {
      setApiResponse([]);
      setIsListLoading(false);
    }
  }, [dispatch, propertySelector, form.property_type, form.gre_mobile_otp]);

  const filterDetail = () => {
    if (!handleValid()) {
      setIsListLoading(true);
      dispatch(getPropertyFilterList(buildListParams(0)));
      setPage(0);
    }
  };

  const filterReset = () => {
    setIsListLoading(true);
    setForm({
      start_date: "",
      end_date: "",
      location: "",
      property_name: "",
      property_type: "",
      gre_mobile_otp: "",
      site: "",
    });
    setFormVaild({
      ...formVaild,
      end_date: "",
    });
    if (isAdmin) {
      dispatch(getAllProperty(buildListParams(0)));
    } else {
      dispatch(getAgentProperty(buildListParams(0)));
    }

    setPage(0);
  };

  const handleValid = () => {
    let err = { ...formVaild };
    let formError = false;
    if (form.start_date !== "") {
      if (form.end_date === "") {
        formError = true;
        err["end_date"] = strings.endDateErr;
      }
    }
    setFormVaild(err);
    return formError;
  };

  const applyOtpChangeToList = (propertyId, enabled) => {
    const otpFilter = form.gre_mobile_otp;
    const removedByFilter =
      (otpFilter === "1" && !enabled) ||
      (otpFilter === "2" && enabled);

    setApiResponse((prev) => {
      if (removedByFilter) {
        return prev.filter((item) => !matchesPropertyId(item, propertyId));
      }
      return prev.map((item) =>
        matchesPropertyId(item, propertyId)
          ? { ...item, gre_mobile_otp_enabled: enabled }
          : item,
      );
    });
  };

  const handleGreMobileOtpToggle = async (propertyId, enabled, propertyTitle) => {
    const previous = apiResponse;
    applyOtpChangeToList(propertyId, enabled);

    const success = await dispatch(
      propertyGreMobileOtpUpdate({
        property_id: propertyId,
        gre_mobile_otp_enabled: enabled,
        property_title: propertyTitle,
      }),
    );

    if (!success) {
      setApiResponse(previous);
      return;
    }

    refreshPropertyList(page);
  };

  const emptyOtpSmsForm = () => ({
    auth_key: "",
    sender_id: "",
    api_url: "",
    route: "1",
    template_id: "",
    otp_message: "",
    default_site_name: "",
    sms_mode: "off",
  });

  const closedOtpSmsConfigModalState = () => ({
    open: false,
    propertyId: null,
    propertyTitle: "",
    otpEnabled: false,
    loading: false,
    saving: false,
    clearing: false,
    form: emptyOtpSmsForm(),
    meta: {
      configured: false,
      has_auth_key: false,
      auth_key_hint: "",
      encryption_configured: true,
      is_production_backend: false,
      is_staging_backend: false,
    },
    error: "",
  });

  const closeOtpSmsConfigModal = () => {
    setOtpSmsConfigModal((current) => {
      if (current.saving || current.clearing) {
        return current;
      }
      return closedOtpSmsConfigModalState();
    });
    setOtpSmsMissingCredsConfirm({ open: false, modeLabel: "" });
    setOtpSmsProdDevModeConfirm({ open: false, modeLabel: "" });
    setOtpSmsStagingLiveConfirm({ open: false });
  };

  const openOtpSmsConfigModal = async (item) => {
    const propertyId = normalizeMongoId(item?._id ?? item?.property_id);
    if (!propertyId) {
      errorToast(strings.otpSmsConfigLoadFailedTitle);
      return;
    }

    setOtpSmsConfigModal({
      open: true,
      propertyId,
      propertyTitle: item?.property_title || "",
      otpEnabled: Boolean(item?.gre_mobile_otp_enabled),
      loading: true,
      saving: false,
      clearing: false,
      form: emptyOtpSmsForm(),
      meta: {
        configured: false,
        has_auth_key: false,
        auth_key_hint: "",
        encryption_configured: true,
        is_production_backend: false,
        is_staging_backend: false,
      },
      error: "",
    });

    const config = await dispatch(
      propertyVisitorOtpSmsConfigGet({ property_id: propertyId }),
    );

    if (!config) {
      setOtpSmsConfigModal((current) => ({
        ...current,
        loading: false,
        error: strings.otpSmsConfigLoadFailedTitle,
      }));
      return;
    }

    const loadedSmsMode = String(config.sms_mode || "off")
      .trim()
      .toLowerCase();
    const isProdBackend = Boolean(config.is_production_backend);
    const isStagingBackend = Boolean(config.is_staging_backend);
    const isProdSmsUi = IS_PRODUCTION_ENVIRONMENT || isProdBackend;
    const isStagingSmsUi =
      !isProdSmsUi && (IS_STAGING_ENVIRONMENT || isStagingBackend);

    let smsMode = loadedSmsMode || "off";
    if (isProdSmsUi && smsMode === "staging") {
      smsMode = "off";
    } else if (isStagingSmsUi && smsMode === "production") {
      smsMode = "staging";
    }

    setOtpSmsConfigModal((current) => ({
      ...current,
      loading: false,
      form: {
        auth_key: "",
        sender_id: config.sender_id || "",
        api_url: config.api_url || "",
        route: config.route || "1",
        template_id: config.template_id || "",
        otp_message: config.otp_message || "",
        default_site_name: config.default_site_name || "",
        sms_mode: smsMode,
      },
      meta: {
        configured: Boolean(config.configured),
        has_auth_key: Boolean(config.has_auth_key),
        auth_key_hint: config.auth_key_hint || "",
        encryption_configured: config.encryption_configured !== false,
        is_production_backend: isProdBackend,
        is_staging_backend: isStagingBackend,
      },
      error: "",
    }));
  };

  const handleOtpSmsConfigFieldChange = (field, value) => {
    setOtpSmsConfigModal((current) => ({
      ...current,
      form: { ...current.form, [field]: value },
      error: "",
    }));
  };

  const otpSmsModeLabel = (mode) => {
    if (mode === "production") {
      return strings.otpSmsConfigSmsModeProduction;
    }
    if (mode === "staging") {
      return strings.otpSmsConfigSmsModeStaging;
    }
    return strings.otpSmsConfigSmsModeOff;
  };

  const getDefaultLiveSmsMode = () => {
    if (isProdSmsEnvironment) {
      return "production";
    }
    if (isStagingSmsEnvironment) {
      return "staging";
    }
    const current = String(otpSmsConfigModal.form.sms_mode || "")
      .trim()
      .toLowerCase();
    if (current === "production" || current === "staging") {
      return current;
    }
    return "staging";
  };

  const isPhysicalSmsMode = (mode) => {
    const normalized = String(mode || "")
      .trim()
      .toLowerCase();
    return normalized === "staging" || normalized === "production";
  };

  const handleOtpSmsDeliveryToggle = (wantsPhysicalSms) => {
    if (otpSmsConfigModal.saving || otpSmsConfigModal.clearing) {
      return;
    }
    handleOtpSmsConfigFieldChange(
      "sms_mode",
      wantsPhysicalSms ? getDefaultLiveSmsMode() : "off",
    );
  };

  const submitOtpSmsConfigSave = async () => {
    const { propertyId, form } = otpSmsConfigModal;
    if (!propertyId || otpSmsConfigModal.saving) {
      return;
    }

    setOtpSmsMissingCredsConfirm({ open: false, modeLabel: "" });
    setOtpSmsProdDevModeConfirm({ open: false, modeLabel: "" });
    setOtpSmsStagingLiveConfirm({ open: false });
    setOtpSmsConfigModal((current) => ({
      ...current,
      saving: true,
      error: "",
    }));

    try {
      const smsMode = String(form.sms_mode || "").trim().toLowerCase();
      let safeSmsMode = smsMode;
      if (isProdSmsEnvironment && smsMode === "staging") {
        safeSmsMode = "off";
      } else if (isStagingSmsEnvironment && smsMode === "production") {
        safeSmsMode = "staging";
      }

      const saved = await dispatch(
        propertyVisitorOtpSmsConfigUpdate({
          property_id: propertyId,
          ...form,
          sms_mode: safeSmsMode || "off",
        }),
      );

      if (!saved) {
        setOtpSmsConfigModal((current) => ({
          ...current,
          saving: false,
        }));
        return;
      }

      setOtpSmsConfigModal(closedOtpSmsConfigModalState());
    } catch (_error) {
      setOtpSmsConfigModal((current) => ({
        ...current,
        saving: false,
        error: strings.otpSmsConfigSaveFailedTitle,
      }));
    }
  };

  const handleOtpSmsConfigSave = async () => {
    const { propertyId, form, meta, otpEnabled } = otpSmsConfigModal;
    if (!propertyId || otpSmsConfigModal.saving) {
      return;
    }

    const smsMode = String(form.sms_mode || "").trim().toLowerCase();
    let effectiveSmsMode = smsMode;
    if (isProdSmsEnvironment && smsMode === "staging") {
      effectiveSmsMode = "off";
    } else if (isStagingSmsEnvironment && smsMode === "production") {
      effectiveSmsMode = "staging";
    }

    const wantsLiveSms =
      effectiveSmsMode === "production" || effectiveSmsMode === "staging";
    const wantsDevOtpMode = effectiveSmsMode === "off";
    const hasSender = Boolean(String(form.sender_id || "").trim());
    const hasAuthKey =
      Boolean(meta.has_auth_key) || Boolean(String(form.auth_key || "").trim());
    const hasCredentials = hasSender && hasAuthKey;

    if (wantsLiveSms && !hasCredentials) {
      setOtpSmsProdDevModeConfirm({ open: false, modeLabel: "" });
      setOtpSmsStagingLiveConfirm({ open: false });
      setOtpSmsMissingCredsConfirm({
        open: true,
        modeLabel: otpSmsModeLabel(effectiveSmsMode),
      });
      return;
    }

    // Production DB: warn before Off while OTP is on (in-app OTP only).
    if (
      isProdSmsEnvironment &&
      otpEnabled &&
      wantsDevOtpMode &&
      !otpSmsProdDevModeConfirm.open
    ) {
      setOtpSmsMissingCredsConfirm({ open: false, modeLabel: "" });
      setOtpSmsStagingLiveConfirm({ open: false });
      setOtpSmsProdDevModeConfirm({
        open: true,
        modeLabel: otpSmsModeLabel(effectiveSmsMode),
      });
      return;
    }

    // Physical SMS (staging or production) — warn about vendor deduction.
    if (
      wantsLiveSms &&
      !otpSmsStagingLiveConfirm.open
    ) {
      setOtpSmsMissingCredsConfirm({ open: false, modeLabel: "" });
      setOtpSmsProdDevModeConfirm({ open: false, modeLabel: "" });
      setOtpSmsStagingLiveConfirm({ open: true });
      return;
    }

    await submitOtpSmsConfigSave();
  };

  const closeOtpSmsMissingCredsConfirm = () => {
    if (otpSmsConfigModal.saving) {
      return;
    }
    setOtpSmsMissingCredsConfirm({ open: false, modeLabel: "" });
  };

  const closeOtpSmsProdDevModeConfirm = () => {
    if (otpSmsConfigModal.saving) {
      return;
    }
    setOtpSmsProdDevModeConfirm({ open: false, modeLabel: "" });
  };

  const closeOtpSmsStagingLiveConfirm = () => {
    if (otpSmsConfigModal.saving) {
      return;
    }
    setOtpSmsStagingLiveConfirm({ open: false });
  };

  const handleOtpSmsConfigClear = async () => {
    const { propertyId } = otpSmsConfigModal;
    if (!propertyId || otpSmsConfigModal.clearing) {
      return;
    }

    const confirmed = window.confirm(strings.otpSmsConfigClearConfirm);
    if (!confirmed) {
      return;
    }

    setOtpSmsConfigModal((current) => ({
      ...current,
      clearing: true,
      error: "",
    }));

    const cleared = await dispatch(
      propertyVisitorOtpSmsConfigClear({ property_id: propertyId }),
    );

    if (!cleared) {
      setOtpSmsConfigModal((current) => ({
        ...current,
        clearing: false,
      }));
      return;
    }

    setOtpSmsConfigModal(closedOtpSmsConfigModalState());
  };

  // Uploads/replaces GRE banner for a property via multipart FormData.
  const handleGreBannerUpload = async (propertyId, file) => {
    if (!file) {
      return false;
    }

    const normalizedPropertyId = normalizeMongoId(propertyId);
    if (!normalizedPropertyId) {
      errorToast(strings.greBannerUpdateFailedTitle);
      return false;
    }

    const formData = new FormData();
    formData.append("property_id", normalizedPropertyId);
    formData.append("gre_banner", file);

    const result = await dispatch(propertyGreBannerUpdate(formData));
    if (!result) {
      return false;
    }

    if (result.property_media_base_url) {
      setPropertyMediaBaseUrl(result.property_media_base_url);
    }

    setApiResponse((prev) =>
      prev.map((item) =>
        matchesPropertyId(item, propertyId)
          ? {
              ...item,
              gre_banner: result.gre_banner,
              gre_banner_url: result.gre_banner_url,
              gre_banner_cache: Date.now(),
            }
          : item,
      ),
    );

    return true;
  };

  const revokeBannerUploadPreview = (previewUrl) => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  const closeBannerUploadModal = () => {
    setBannerUploadModal((current) => {
      if (current.uploading) {
        return current;
      }

      revokeBannerUploadPreview(current.previewUrl);
      if (greBannerFileInputRef.current) {
        greBannerFileInputRef.current.value = "";
      }

      return {
        open: false,
        propertyId: null,
        propertyTitle: "",
        hasExistingBanner: false,
        selectedFile: null,
        previewUrl: "",
        error: "",
        uploading: false,
      };
    });
  };

  const openBannerUploadModal = (item) => {
    const propertyId = normalizeMongoId(item?._id ?? item?.property_id);
    if (!propertyId) {
      return;
    }

    setBannerUploadModal((current) => {
      revokeBannerUploadPreview(current.previewUrl);
      if (greBannerFileInputRef.current) {
        greBannerFileInputRef.current.value = "";
      }

      return {
        open: true,
        propertyId,
        propertyTitle: item?.property_title || "",
        hasExistingBanner: Boolean(item?.gre_banner),
        selectedFile: null,
        previewUrl: "",
        error: "",
        uploading: false,
      };
    });
  };

  const clearBannerUploadSelection = () => {
    setBannerUploadModal((current) => {
      if (current.uploading) {
        return current;
      }

      revokeBannerUploadPreview(current.previewUrl);
      if (greBannerFileInputRef.current) {
        greBannerFileInputRef.current.value = "";
      }

      return {
        ...current,
        selectedFile: null,
        previewUrl: "",
        error: "",
      };
    });
  };

  const handleGreBannerFileSelection = (file) => {
    const validation = validateGreBannerFile(file, {
      invalidFormat: strings.greBannerInvalidFormat,
      fileTooLarge: strings.greBannerFileTooLarge,
    });

    setBannerUploadModal((current) => {
      revokeBannerUploadPreview(current.previewUrl);

      if (!validation.valid) {
        if (greBannerFileInputRef.current) {
          greBannerFileInputRef.current.value = "";
        }

        return {
          ...current,
          selectedFile: null,
          previewUrl: "",
          error: validation.error,
        };
      }

      const previewUrl = URL.createObjectURL(file);
      return {
        ...current,
        selectedFile: file,
        previewUrl,
        error: "",
      };
    });
  };

  const confirmGreBannerUpload = async () => {
    const { propertyId, selectedFile } = bannerUploadModal;
    if (!propertyId || !selectedFile) {
      return;
    }

    const validation = validateGreBannerFile(selectedFile, {
      invalidFormat: strings.greBannerInvalidFormat,
      fileTooLarge: strings.greBannerFileTooLarge,
    });

    if (!validation.valid) {
      setBannerUploadModal((current) => ({
        ...current,
        error: validation.error,
      }));
      return;
    }

    setBannerUploadModal((current) => ({ ...current, uploading: true, error: "" }));

    const success = await handleGreBannerUpload(propertyId, selectedFile);

    if (!success) {
      setBannerUploadModal((current) => ({ ...current, uploading: false }));
      return;
    }

    setBannerUploadModal((current) => {
      revokeBannerUploadPreview(current.previewUrl);
      if (greBannerFileInputRef.current) {
        greBannerFileInputRef.current.value = "";
      }

      return {
        open: false,
        propertyId: null,
        propertyTitle: "",
        hasExistingBanner: false,
        selectedFile: null,
        previewUrl: "",
        error: "",
        uploading: false,
      };
    });
  };

  // Opens confirm modal before GRE banner is removed from S3 and the database.
  const openBannerRemoveConfirm = (item) => {
    const propertyId = normalizeMongoId(item?._id ?? item?.property_id);
    if (!propertyId) {
      return;
    }

    setBannerRemoveConfirm({
      open: true,
      propertyId,
      propertyTitle: item?.property_title || "",
      removing: false,
    });
  };

  const closeBannerRemoveConfirm = () => {
    setBannerRemoveConfirm((current) => {
      if (current.removing) {
        return current;
      }

      return {
        open: false,
        propertyId: null,
        propertyTitle: "",
        removing: false,
      };
    });
  };

  // Optimistic UI clear; rolls back if propertyGreBannerRemove API fails.
  const confirmGreBannerRemove = async () => {
    const propertyId = bannerRemoveConfirm.propertyId;
    if (!propertyId) {
      return;
    }

    setBannerRemoveConfirm((current) => ({ ...current, removing: true }));

    const previous = apiResponse;
    setApiResponse((prev) =>
      prev.map((item) =>
        matchesPropertyId(item, propertyId)
          ? { ...item, gre_banner: null, gre_banner_url: null, gre_banner_cache: null }
          : item,
      ),
    );

    const success = await dispatch(
      propertyGreBannerRemove({ property_id: propertyId }),
    );

    if (!success) {
      setApiResponse(previous);
      setBannerRemoveConfirm((current) => ({ ...current, removing: false }));
      return;
    }

    setBannerRemoveConfirm({
      open: false,
      propertyId: null,
      propertyTitle: "",
      removing: false,
    });
  };

  useEffect(() => {
    if (isAdmin) {
      dispatch(
        getReportPropertyList({ limit: 10, offset: 0, user_id, role_id }),
      );
    }
  }, [dispatch]);

  const handleInputField = (e) => {
    const nextFormState = {
      ...form,
      [e.target.name]: e.target.value,
    };
    setForm(nextFormState);
    if (e.target.name === "end_date") {
      setFormVaild({
        ...formVaild,
        end_date: "",
      });
    }
  };

  const handlePageClick = (newOffset) => {
    setPage(newOffset);
    refreshPropertyList(newOffset);
  };

  useEffect(() => {
    if (form.start_date === "") {
      setEndDateDisable(true);
    } else {
      setEndDateDisable(false);
    }
  }, [form.start_date]);

  useEffect(() => {
    seo({
      title: "Property Listing",
      metaDescription: "All registered property list",
      canonicalLink: window.location.href,
    });
  }, []);
  const formatPropertyModalMessage = (template, propertyTitle) => {
    const property = String(propertyTitle || "").trim() || "This property";
    return String(template || "").replace(/\{\{property\}\}/g, property);
  };

  return (
    <div className="page-body justo-property-page">
      <Breadcrumb
        title={strings.Property}
        titleactive={strings.propertyListing}
      />
      <div className="container-fluid justo-property">
        <div className="justo-property__card">
          <div className="justo-property__top">
            <div className="justo-property__title-block">
              <h1 className="justo-property__title">{strings.propertyList}</h1>
              <p className="justo-property__subtitle">
                {strings.propertyListSubtitle}
              </p>
            </div>
            <div className="justo-property__top-actions">
              {create ? (
                <button
                  type="button"
                  className="justo-property__btn justo-property__btn--solid"
                  onClick={() => navigatpage("/addnewproperty")}
                >
                  {strings.addNewProperty}
                </button>
              ) : null}
            </div>
          </div>

          <form
            className="justo-property__filters"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="justo-property__filters-grid">
              <label className="justo-property__field">
                <span className="justo-property__label">{strings.startDate}</span>
                <input
                  className="form-control justo-property__input"
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleInputField}
                />
              </label>
              <label className="justo-property__field">
                <span className="justo-property__label">{strings.endDate}</span>
                <input
                  className="form-control justo-property__input"
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleInputField}
                  min={form.start_date}
                  disabled={endDateDisable}
                />
                {formVaild.end_date ? (
                  <span className="justo-property__error">
                    {formVaild.end_date}
                  </span>
                ) : null}
              </label>
              <label className="justo-property__field">
                <span className="justo-property__label">
                  {strings.searchBylocality}
                </span>
                <input
                  className="form-control justo-property__input"
                  placeholder={strings.searchlocality}
                  type="text"
                  autoComplete="off"
                  name="location"
                  value={form.location}
                  onChange={handleInputField}
                />
              </label>
              <div className="justo-property__field">
                <span className="justo-property__label">
                  {strings.searchByProjectName}
                </span>
                <Select
                  className="property-listing-filter-select"
                  classNamePrefix="property-listing-select"
                  isSearchable
                  isClearable
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                  onInputChange={(e) => {
                    if (isAdmin) {
                      if (e.length > 1) {
                        dispatch(
                          getReportPropertyList({
                            search_site: e,
                            isLoadMore: true,
                          }),
                        );
                      } else {
                        dispatch(
                          getReportPropertyList({
                            limit: 10,
                            offset: 0,
                            isLoadMore: true,
                          }),
                        );
                      }
                    } else {
                      if (e.length > 1) {
                        dispatch(
                          getAgentProperty({
                            search_site: e,
                            isLoadMore: true,
                          }),
                        );
                      } else {
                        dispatch(
                          getAgentProperty({
                            limit: "",
                            offset: 0,
                            isLoadMore: true,
                          }),
                        );
                      }
                    }
                  }}
                  placeholder={strings.searchByProjectName}
                  options={propertyList}
                  getOptionLabel={(option) => `${option.property_title}`}
                  getOptionValue={(option) => option._id}
                  name="site"
                  value={form.site}
                  onChange={(e) => {
                    setForm({ ...form, site: e });
                  }}
                />
              </div>
              <label className="justo-property__field">
                <span className="justo-property__label">
                  {strings.searchByStatus}
                </span>
                <select
                  className="form-control justo-property__input"
                  name="property_type"
                  value={form.property_type}
                  onChange={(event) => handleInputField(event)}
                >
                  <option value="">{strings.selectstatus}</option>
                  <option value={"1"}>{strings.active}</option>
                  <option value={"2"}>{strings.deactive}</option>
                </select>
              </label>
              {isSuperAdmin ? (
                <label className="justo-property__field">
                  <span className="justo-property__label">
                    {strings.searchByOtpVerification}
                  </span>
                  <select
                    className="form-control justo-property__input"
                    name="gre_mobile_otp"
                    value={form.gre_mobile_otp}
                    onChange={(event) => handleInputField(event)}
                  >
                    <option value="">{strings.selectOtpVerification}</option>
                    <option value="1">{strings.greMobileOtpEnabled}</option>
                    <option value="2">{strings.greMobileOtpDisabled}</option>
                  </select>
                </label>
              ) : null}
            </div>
            <div className="justo-property__filter-footer">
              <div className="justo-property__filter-actions">
                <button
                  type="button"
                  className="justo-property__btn justo-property__btn--solid"
                  onClick={filterDetail}
                >
                  {strings.search}
                </button>
                <button
                  type="button"
                  className="justo-property__btn justo-property__btn--ghost"
                  onClick={filterReset}
                >
                  {strings.reset}
                </button>
              </div>
            </div>
          </form>

          <div className="justo-property__scroll property-listing-card__scroll">
            {isListLoading ? (
              <TableListSkeleton rows={10} columns={8} />
            ) : (
              <>
                <table className="table property-listing-table">
                  <thead>
                    <tr>
                      <th scope="col" className="property-listing-table__project-col">
                        {strings.projectName}
                      </th>
                      <th scope="col" className="property-listing-table__date-col">
                        {strings.createdDate}
                      </th>
                      <th scope="col" className="property-listing-table__metric-col">
                        {strings.vistior}
                      </th>
                      <th scope="col" className="property-listing-table__metric-col">
                        {strings.sitevisit}
                      </th>
                      <th scope="col" className="property-listing-table__metric-col">
                        {strings.bookingVisit}
                      </th>
                      <th scope="col" className="property-listing-table__metric-col">
                        {strings.closeVisit}
                      </th>
                      <th scope="col" className="property-listing-table__status-col">
                        {strings.status}
                      </th>
                      {isSuperAdmin && (
                        <th scope="col" className="property-listing-table__otp-col">
                          <span className="property-listing-table__otp-title">
                            {strings.greMobileOtpTitle}
                          </span>
                          <span className="property-listing-table__otp-hint">
                            {strings.greMobileOtpHint}
                          </span>
                        </th>
                      )}
                      {isSuperAdmin && (
                        <th scope="col" className="property-listing-table__banner-col">
                          <span className="property-listing-table__otp-title">
                            {strings.greBannerTitle}
                          </span>
                          <span className="property-listing-table__otp-hint">
                            {strings.greBannerHint}
                          </span>
                        </th>
                      )}
                      <th scope="col" className="property-listing-table__budget-col">
                        {strings.budget}
                      </th>
                      <th scope="col" className="property-listing-table__actions-col">
                        {strings.action}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {apiResponse &&
                          apiResponse.map((item, index) => {
                            const isOtpEnabled = Boolean(
                              item.gre_mobile_otp_enabled,
                            );
                            const hasBudget =
                              item?.min_rate || item?.max_rate;

                            return (
                              <tr key={index}>
                                <td className="property-listing-table__project property-listing-table__project-col">
                                  {renderPropertyCell(
                                    item.property_title,
                                    "property-listing-table__project-name",
                                  )}
                                </td>
                                <td className="property-listing-table__date property-listing-table__date-col">
                                  {renderPropertyCell(
                                    moment(item.createdDate).format(
                                      "DD-MM-yyyy hh:mm A",
                                    ),
                                    "property-listing-table__date",
                                  )}
                                </td>
                                <td className="property-listing-table__metric property-listing-table__metric-col">
                                  {renderPropertyCell(
                                    item.total_visitor,
                                    "property-listing-table__metric",
                                  )}
                                </td>
                                <td className="property-listing-table__metric property-listing-table__metric-col">
                                  {renderPropertyCell(
                                    item.site_visit,
                                    "property-listing-table__metric",
                                  )}
                                </td>
                                <td className="property-listing-table__metric property-listing-table__metric-col">
                                  {renderPropertyCell(
                                    item.booking_visit,
                                    "property-listing-table__metric",
                                  )}
                                </td>
                                <td className="property-listing-table__metric property-listing-table__metric-col">
                                  {renderPropertyCell(
                                    item.close_visit,
                                    "property-listing-table__metric",
                                  )}
                                </td>
                                <td className="property-listing-table__status-col">
                                  <span
                                    className={`property-status-badge ${
                                      item.status
                                        ? "property-status-badge--active"
                                        : "property-status-badge--inactive"
                                    }`}
                                    title={
                                      item.status
                                        ? strings.active
                                        : strings.deactive
                                    }
                                  >
                                    <i
                                      className={`fa ${
                                        item.status
                                          ? "fa-check-circle"
                                          : "fa-exclamation-circle"
                                      } property-status-badge__icon`}
                                      aria-hidden="true"
                                    />
                                    <span className="property-status-badge__label">
                                      {item.status
                                        ? strings.active
                                        : strings.deactive}
                                    </span>
                                  </span>
                                </td>
                                {isSuperAdmin && (
                                  <td className="property-listing-table__otp-col">
                                    <div className="property-otp-cell">
                                      <label
                                        className="property-otp-toggle"
                                        title={
                                          isOtpEnabled
                                            ? strings.greMobileOtpEnabled
                                            : strings.greMobileOtpDisabled
                                        }
                                      >
                                        <input
                                          className="property-otp-toggle__input"
                                          type="checkbox"
                                          checked={isOtpEnabled}
                                          onChange={(e) =>
                                            handleGreMobileOtpToggle(
                                              item._id,
                                              e.target.checked,
                                              item.property_title,
                                            )
                                          }
                                        />
                                        <span className="property-otp-toggle__track">
                                          <span className="property-otp-toggle__thumb" />
                                        </span>
                                        <span className="property-otp-toggle__label">
                                          {isOtpEnabled
                                            ? strings.greMobileOtpOn
                                            : strings.greMobileOtpOff}
                                        </span>
                                      </label>
                                      <button
                                        type="button"
                                        className="property-otp-config-btn"
                                        title={strings.otpSmsConfigButton}
                                        onClick={() =>
                                          openOtpSmsConfigModal(item)
                                        }
                                      >
                                        <i
                                          className="fa fa-cog"
                                          aria-hidden="true"
                                        />
                                        <span>{strings.otpSmsConfigButton}</span>
                                      </button>
                                    </div>
                                  </td>
                                )}
                                {isSuperAdmin && (
                                  <td className="property-listing-table__banner-col">
                                    <div className="property-banner-upload">
                                      <div
                                        className={`property-banner-upload__actions${
                                          item.gre_banner
                                            ? ""
                                            : " property-banner-upload__actions--single"
                                        }`}
                                      >
                                        {item.gre_banner ? (
                                          <button
                                            type="button"
                                            className="property-banner-upload__preview"
                                            onClick={() =>
                                              handleGreBannerPreview(item)
                                            }
                                          >
                                            {strings.greBannerPreview}
                                          </button>
                                        ) : null}
                                        <button
                                          type="button"
                                          className="property-banner-upload__btn"
                                          onClick={() =>
                                            openBannerUploadModal(item)
                                          }
                                        >
                                          {item.gre_banner
                                            ? strings.greBannerReplace
                                            : strings.greBannerUpload}
                                        </button>
                                        {item.gre_banner ? (
                                          <button
                                            type="button"
                                            className="property-banner-upload__remove"
                                            onClick={() =>
                                              openBannerRemoveConfirm(item)
                                            }
                                          >
                                            {strings.greBannerRemove}
                                          </button>
                                        ) : null}
                                      </div>
                                    </div>
                                  </td>
                                )}
                                <td
                                  className={`property-listing-table__budget property-listing-table__budget-col${
                                    hasBudget
                                      ? ""
                                      : " property-listing-table__budget--empty"
                                  }`}
                                >
                                  {hasBudget ? (
                                    <span className="property-listing-table__cell property-listing-table__budget">
                                      {item?.min_rate}
                                      {item.min_rate ? item.min_rate_type : null}
                                      {" - "}
                                      {item?.max_rate}
                                      {item.max_rate ? item.max_rate_type : null}
                                    </span>
                                  ) : (
                                    <span className="property-listing-table__cell property-listing-table__empty">
                                      —
                                    </span>
                                  )}
                                </td>
                                <td className="property-listing-table__actions-col">
                                  <div className="property-listing-table__actions">
                                    <span className="property-listing-table__action-slot">
                                      {view ? (
                                        <button
                                          type="button"
                                          className="property-action-btn"
                                          title={strings.viewProperty}
                                          onClick={() => {
                                            navigatpage(
                                              "/viewproperty/" + item._id,
                                            );
                                          }}
                                        >
                                          <i
                                            className="fa fa-eye"
                                            aria-hidden="true"
                                          />
                                        </button>
                                      ) : (
                                        <span
                                          className="property-action-btn property-action-btn--placeholder"
                                          aria-hidden="true"
                                        />
                                      )}
                                    </span>
                                    <span className="property-listing-table__action-slot">
                                      {edit && item?.status !== false ? (
                                        <button
                                          type="button"
                                          className="property-action-btn"
                                          title={strings.editProperty}
                                          onClick={() => {
                                            navigatpage(
                                              "/editproperty/" + item._id,
                                            );
                                          }}
                                        >
                                          <i
                                            className="fa fa-edit"
                                            aria-hidden="true"
                                          />
                                        </button>
                                      ) : (
                                        <span
                                          className="property-action-btn property-action-btn--placeholder"
                                          aria-hidden="true"
                                        />
                                      )}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}

                  </tbody>
                </table>
                <TableDataNotFound data={apiResponse} />
              </>
            )}
          </div>
          <Pagination
            totalCount={propertySelector?.response?.total_data}
            listData={apiResponse}
            onPageChange={handlePageClick}
            itemOffset={page}
            setItemOffset={setPage}
                  />
        </div>
      </div>
      {bannerPreview.open ? (
        <div
          className="property-banner-preview-modal"
          role="dialog"
          aria-modal="true"
          aria-label={strings.greBannerPreview}
          onClick={closeBannerPreview}
        >
          <div
            className="property-banner-preview-modal__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            {bannerPreview.title ? (
              <div className="property-banner-preview-modal__title">
                {bannerPreview.title}
              </div>
            ) : null}
            {bannerPreview.url ? (
              <img
                src={bannerPreview.url}
                alt={`${bannerPreview.title || "Property"} GRE banner`}
                className="property-banner-preview-modal__image"
              />
            ) : null}
            <button
              type="button"
              className="property-banner-preview-modal__close"
              onClick={closeBannerPreview}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
      {bannerUploadModal.open ? (
        <div
          className="property-banner-upload-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="greBannerUploadModalTitle"
          onClick={closeBannerUploadModal}
        >
          <div
            className="property-banner-upload-modal__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="property-banner-upload-modal__header">
              <h5
                className="property-banner-upload-modal__title"
                id="greBannerUploadModalTitle"
              >
                {bannerUploadModal.hasExistingBanner
                  ? strings.greBannerUploadModalReplaceTitle
                  : strings.greBannerUploadModalTitle}
              </h5>
              <button
                type="button"
                className="property-banner-upload-modal__close"
                aria-label={strings.cancel}
                onClick={closeBannerUploadModal}
                disabled={bannerUploadModal.uploading}
              >
                ×
              </button>
            </div>
            <div className="property-banner-upload-modal__body">
              {bannerUploadModal.propertyTitle ? (
                <p className="property-banner-upload-modal__property-name">
                  {bannerUploadModal.propertyTitle}
                </p>
              ) : null}
              <ul className="property-banner-upload-modal__guidelines">
                <li>{strings.greBannerUploadRecommendedSize}</li>
                <li>{strings.greBannerUploadFormats}</li>
                <li>{strings.greBannerUploadMaxSize}</li>
              </ul>
              <input
                ref={greBannerFileInputRef}
                type="file"
                accept={GRE_BANNER_ALLOWED_MIME_TYPES.join(",")}
                className="property-banner-upload-modal__input"
                id="gre-banner-upload-input"
                disabled={bannerUploadModal.uploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleGreBannerFileSelection(file);
                  }
                }}
              />
              {bannerUploadModal.previewUrl ? (
                <div className="property-banner-upload-modal__preview-wrap">
                  <div className="property-banner-upload-modal__dropzone property-banner-upload-modal__dropzone--has-preview property-banner-upload-modal__dropzone--preview-only">
                    <img
                      src={bannerUploadModal.previewUrl}
                      alt={`${bannerUploadModal.propertyTitle || "Property"} GRE banner preview`}
                      className="property-banner-upload-modal__preview"
                    />
                  </div>
                  <button
                    type="button"
                    className="property-banner-upload-modal__clear-btn"
                    onClick={clearBannerUploadSelection}
                    disabled={bannerUploadModal.uploading}
                  >
                    {strings.greBannerUploadClearSelection}
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="gre-banner-upload-input"
                  className="property-banner-upload-modal__dropzone"
                >
                  <span className="property-banner-upload-modal__dropzone-title">
                    {strings.greBannerUploadSelectFile}
                  </span>
                  <span className="property-banner-upload-modal__dropzone-hint">
                    {strings.greBannerUploadSelectFileHint}
                  </span>
                </label>
              )}
              {bannerUploadModal.error ? (
                <p
                  className="property-banner-upload-modal__error"
                  role="alert"
                >
                  {bannerUploadModal.error}
                </p>
              ) : null}
            </div>
            <div className="property-banner-upload-modal__footer">
              <button
                type="button"
                className="property-banner-upload-modal__btn property-banner-upload-modal__btn--cancel"
                onClick={closeBannerUploadModal}
                disabled={bannerUploadModal.uploading}
              >
                {strings.cancel}
              </button>
              <button
                type="button"
                className="property-banner-upload-modal__btn property-banner-upload-modal__btn--confirm"
                onClick={confirmGreBannerUpload}
                disabled={
                  bannerUploadModal.uploading || !bannerUploadModal.selectedFile
                }
              >
                {strings.greBannerUploadSubmit}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {bannerRemoveConfirm.open ? (
        <div
          className="property-banner-remove-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="greBannerRemoveModalTitle"
          onClick={closeBannerRemoveConfirm}
        >
          <div
            className="property-banner-remove-modal__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="property-banner-remove-modal__header">
              <h5
                className="property-banner-remove-modal__title"
                id="greBannerRemoveModalTitle"
              >
                {strings.greBannerRemoveTitle}
              </h5>
              <button
                type="button"
                className="property-banner-remove-modal__close"
                aria-label={strings.cancel}
                onClick={closeBannerRemoveConfirm}
                disabled={bannerRemoveConfirm.removing}
              >
                ×
              </button>
            </div>
            <div className="property-banner-remove-modal__body">
              {bannerRemoveConfirm.propertyTitle ? (
                <p className="property-banner-remove-modal__property-name">
                  {bannerRemoveConfirm.propertyTitle}
                </p>
              ) : null}
              <p className="property-banner-remove-modal__message">
                {formatPropertyModalMessage(
                  strings.greBannerRemoveMessage,
                  bannerRemoveConfirm.propertyTitle,
                )}
              </p>
            </div>
            <div className="property-banner-remove-modal__footer">
              <button
                type="button"
                className="property-banner-remove-modal__btn property-banner-remove-modal__btn--cancel"
                onClick={closeBannerRemoveConfirm}
                disabled={bannerRemoveConfirm.removing}
              >
                {strings.cancel}
              </button>
              <button
                type="button"
                className="property-banner-remove-modal__btn property-banner-remove-modal__btn--confirm"
                onClick={confirmGreBannerRemove}
                disabled={bannerRemoveConfirm.removing}
              >
                {strings.greBannerRemoveConfirm}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {otpSmsConfigModal.open ? (
        <div
          className="property-otp-sms-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="otpSmsConfigModalTitle"
          onClick={closeOtpSmsConfigModal}
        >
          <div
            className="property-otp-sms-modal__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="property-otp-sms-modal__header">
              <div>
                <h5
                  className="property-otp-sms-modal__title"
                  id="otpSmsConfigModalTitle"
                >
                  {strings.otpSmsConfigModalTitle}
                </h5>
                <p className="property-otp-sms-modal__subtitle">
                  {strings.otpSmsConfigModalSubtitle}
                </p>
              </div>
              <button
                type="button"
                className="property-otp-sms-modal__close"
                aria-label={strings.cancel}
                onClick={closeOtpSmsConfigModal}
                disabled={
                  otpSmsConfigModal.saving || otpSmsConfigModal.clearing
                }
              >
                ×
              </button>
            </div>
            <div className="property-otp-sms-modal__body">
              {otpSmsConfigModal.propertyTitle ? (
                <p className="property-otp-sms-modal__property-name">
                  {otpSmsConfigModal.propertyTitle}
                </p>
              ) : null}
              <p
                className={`property-otp-sms-modal__status${
                  otpSmsConfigModal.meta.configured
                    ? " property-otp-sms-modal__status--configured"
                    : ""
                }`}
              >
                {otpSmsConfigModal.meta.configured
                  ? strings.otpSmsConfigConfigured
                  : strings.otpSmsConfigNotConfigured}
              </p>
              {!otpSmsConfigModal.meta.encryption_configured ? (
                <p className="property-otp-sms-modal__error">
                  {strings.otpSmsConfigEncryptionMissing}
                </p>
              ) : null}
              {otpSmsConfigModal.loading ? (
                <p className="property-otp-sms-modal__loading">Loading…</p>
              ) : (
                <div className="property-otp-sms-modal__grid">
                  <label className="property-otp-sms-modal__field">
                    <span>{strings.otpSmsConfigAuthKey}</span>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={otpSmsConfigModal.form.auth_key}
                      placeholder={strings.otpSmsConfigAuthKeyPlaceholder}
                      disabled={
                        otpSmsConfigModal.saving || otpSmsConfigModal.clearing
                      }
                      onChange={(e) =>
                        handleOtpSmsConfigFieldChange(
                          "auth_key",
                          e.target.value,
                        )
                      }
                    />
                    {otpSmsConfigModal.meta.has_auth_key &&
                    otpSmsConfigModal.meta.auth_key_hint ? (
                      <em>
                        {strings.otpSmsConfigAuthKeyHint.replace(
                          "{{hint}}",
                          otpSmsConfigModal.meta.auth_key_hint,
                        )}
                      </em>
                    ) : null}
                  </label>
                  <label className="property-otp-sms-modal__field">
                    <span>{strings.otpSmsConfigSenderId}</span>
                    <input
                      type="text"
                      value={otpSmsConfigModal.form.sender_id}
                      disabled={
                        otpSmsConfigModal.saving || otpSmsConfigModal.clearing
                      }
                      onChange={(e) =>
                        handleOtpSmsConfigFieldChange(
                          "sender_id",
                          e.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="property-otp-sms-modal__field property-otp-sms-modal__field--full">
                    <span>{strings.otpSmsConfigApiUrl}</span>
                    <input
                      type="text"
                      value={otpSmsConfigModal.form.api_url}
                      disabled={
                        otpSmsConfigModal.saving || otpSmsConfigModal.clearing
                      }
                      onChange={(e) =>
                        handleOtpSmsConfigFieldChange("api_url", e.target.value)
                      }
                    />
                  </label>
                  <label className="property-otp-sms-modal__field">
                    <span>{strings.otpSmsConfigRoute}</span>
                    <input
                      type="text"
                      value={otpSmsConfigModal.form.route}
                      disabled={
                        otpSmsConfigModal.saving || otpSmsConfigModal.clearing
                      }
                      onChange={(e) =>
                        handleOtpSmsConfigFieldChange("route", e.target.value)
                      }
                    />
                  </label>
                  <div className="property-otp-sms-modal__field property-otp-sms-modal__field--full">
                    <span>{strings.otpSmsConfigSmsMode}</span>
                    <div
                      className="property-otp-sms-modal__delivery"
                      role="group"
                      aria-label={strings.otpSmsConfigSmsMode}
                    >
                      <button
                        type="button"
                        className={`property-otp-sms-modal__delivery-btn${
                          !isPhysicalSmsMode(otpSmsConfigModal.form.sms_mode)
                            ? " property-otp-sms-modal__delivery-btn--active"
                            : ""
                        }`}
                        disabled={
                          otpSmsConfigModal.saving ||
                          otpSmsConfigModal.clearing
                        }
                        onClick={() => handleOtpSmsDeliveryToggle(false)}
                      >
                        {strings.otpSmsConfigDeliveryDev}
                      </button>
                      <button
                        type="button"
                        className={`property-otp-sms-modal__delivery-btn${
                          isPhysicalSmsMode(otpSmsConfigModal.form.sms_mode)
                            ? " property-otp-sms-modal__delivery-btn--active"
                            : ""
                        }`}
                        disabled={
                          otpSmsConfigModal.saving ||
                          otpSmsConfigModal.clearing
                        }
                        onClick={() => handleOtpSmsDeliveryToggle(true)}
                      >
                        {strings.otpSmsConfigDeliveryPhysical}
                      </button>
                    </div>
                    <div
                      className="property-otp-sms-modal__delivery-hint"
                      aria-live="polite"
                    >
                      <em
                        className={
                          !isPhysicalSmsMode(otpSmsConfigModal.form.sms_mode)
                            ? "is-visible"
                            : undefined
                        }
                        aria-hidden={isPhysicalSmsMode(
                          otpSmsConfigModal.form.sms_mode,
                        )}
                      >
                        {strings.otpSmsConfigDeliveryDevHint}
                      </em>
                      <em
                        className={
                          isPhysicalSmsMode(otpSmsConfigModal.form.sms_mode)
                            ? "is-visible"
                            : undefined
                        }
                        aria-hidden={
                          !isPhysicalSmsMode(otpSmsConfigModal.form.sms_mode)
                        }
                      >
                        {strings.otpSmsConfigDeliveryPhysicalHint}
                      </em>
                    </div>
                  </div>
                  {!isProdSmsEnvironment && !isStagingSmsEnvironment ? (
                    <label
                      className={`property-otp-sms-modal__field${
                        !isPhysicalSmsMode(otpSmsConfigModal.form.sms_mode)
                          ? " property-otp-sms-modal__field--muted"
                          : ""
                      }`}
                    >
                      <span>{strings.otpSmsConfigLiveGateway}</span>
                      <select
                        value={
                          otpSmsConfigModal.form.sms_mode === "production"
                            ? "production"
                            : "staging"
                        }
                        disabled={
                          otpSmsConfigModal.saving ||
                          otpSmsConfigModal.clearing ||
                          !isPhysicalSmsMode(otpSmsConfigModal.form.sms_mode)
                        }
                        onChange={(e) =>
                          handleOtpSmsConfigFieldChange(
                            "sms_mode",
                            e.target.value,
                          )
                        }
                      >
                        <option value="staging">
                          {strings.otpSmsConfigSmsModeStaging}
                        </option>
                        <option value="production">
                          {strings.otpSmsConfigSmsModeProduction}
                        </option>
                      </select>
                    </label>
                  ) : null}
                  <label className="property-otp-sms-modal__field property-otp-sms-modal__field--full">
                    <span>{strings.otpSmsConfigTemplateId}</span>
                    <input
                      type="text"
                      value={otpSmsConfigModal.form.template_id}
                      disabled={
                        otpSmsConfigModal.saving || otpSmsConfigModal.clearing
                      }
                      onChange={(e) =>
                        handleOtpSmsConfigFieldChange(
                          "template_id",
                          e.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="property-otp-sms-modal__field property-otp-sms-modal__field--full">
                    <span>{strings.otpSmsConfigDefaultSite}</span>
                    <input
                      type="text"
                      value={otpSmsConfigModal.form.default_site_name}
                      disabled={
                        otpSmsConfigModal.saving || otpSmsConfigModal.clearing
                      }
                      onChange={(e) =>
                        handleOtpSmsConfigFieldChange(
                          "default_site_name",
                          e.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="property-otp-sms-modal__field property-otp-sms-modal__field--full">
                    <span>{strings.otpSmsConfigMessage}</span>
                    <textarea
                      rows={3}
                      value={otpSmsConfigModal.form.otp_message}
                      disabled={
                        otpSmsConfigModal.saving || otpSmsConfigModal.clearing
                      }
                      onChange={(e) =>
                        handleOtpSmsConfigFieldChange(
                          "otp_message",
                          e.target.value,
                        )
                      }
                    />
                    <em>{strings.otpSmsConfigMessageHint}</em>
                  </label>
                </div>
              )}
              {otpSmsConfigModal.error ? (
                <p className="property-otp-sms-modal__error">
                  {otpSmsConfigModal.error}
                </p>
              ) : null}
            </div>
            <div className="property-otp-sms-modal__footer">
              <button
                type="button"
                className="property-otp-sms-modal__btn property-otp-sms-modal__btn--ghost"
                onClick={closeOtpSmsConfigModal}
                disabled={
                  otpSmsConfigModal.saving || otpSmsConfigModal.clearing
                }
              >
                {strings.cancel}
              </button>
              {otpSmsConfigModal.meta.configured ? (
                <button
                  type="button"
                  className="property-otp-sms-modal__btn property-otp-sms-modal__btn--danger"
                  onClick={handleOtpSmsConfigClear}
                  disabled={
                    otpSmsConfigModal.loading ||
                    otpSmsConfigModal.saving ||
                    otpSmsConfigModal.clearing
                  }
                >
                  {strings.otpSmsConfigClear}
                </button>
              ) : null}
              <button
                type="button"
                className="property-otp-sms-modal__btn property-otp-sms-modal__btn--solid"
                onClick={handleOtpSmsConfigSave}
                disabled={
                  otpSmsConfigModal.loading ||
                  otpSmsConfigModal.saving ||
                  otpSmsConfigModal.clearing
                }
              >
                <i className="fa fa-check" aria-hidden="true" />
                {strings.otpSmsConfigSave}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {otpSmsMissingCredsConfirm.open ? (
        <div
          className="property-otp-sms-confirm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="otpSmsMissingCredsTitle"
          onClick={closeOtpSmsMissingCredsConfirm}
        >
          <div
            className="property-otp-sms-confirm__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="property-otp-sms-confirm__header">
              <h5
                className="property-otp-sms-confirm__title"
                id="otpSmsMissingCredsTitle"
              >
                {strings.otpSmsConfigMissingCredsTitle}
              </h5>
              <button
                type="button"
                className="property-otp-sms-confirm__close"
                aria-label={strings.cancel}
                onClick={closeOtpSmsMissingCredsConfirm}
                disabled={otpSmsConfigModal.saving}
              >
                ×
              </button>
            </div>
            <div className="property-otp-sms-confirm__body">
              {otpSmsConfigModal.propertyTitle ? (
                <p className="property-otp-sms-confirm__property-name">
                  {otpSmsConfigModal.propertyTitle}
                </p>
              ) : null}
              <p className="property-otp-sms-confirm__message">
                {strings.otpSmsConfigMissingCredsMessage.replace(
                  "{{mode}}",
                  otpSmsMissingCredsConfirm.modeLabel ||
                    strings.otpSmsConfigSmsModeProduction,
                )}
              </p>
            </div>
            <div className="property-otp-sms-confirm__footer">
              <button
                type="button"
                className="property-otp-sms-modal__btn property-otp-sms-modal__btn--solid"
                onClick={closeOtpSmsMissingCredsConfirm}
                disabled={otpSmsConfigModal.saving}
              >
                {strings.otpSmsConfigMissingCredsConfirm}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {otpSmsProdDevModeConfirm.open ? (
        <div
          className="property-otp-sms-confirm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="otpSmsProdDevModeTitle"
          onClick={closeOtpSmsProdDevModeConfirm}
        >
          <div
            className="property-otp-sms-confirm__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="property-otp-sms-confirm__header">
              <h5
                className="property-otp-sms-confirm__title"
                id="otpSmsProdDevModeTitle"
              >
                {strings.otpSmsConfigProdDevModeTitle}
              </h5>
              <button
                type="button"
                className="property-otp-sms-confirm__close"
                aria-label={strings.cancel}
                onClick={closeOtpSmsProdDevModeConfirm}
                disabled={otpSmsConfigModal.saving}
              >
                ×
              </button>
            </div>
            <div className="property-otp-sms-confirm__body">
              {otpSmsConfigModal.propertyTitle ? (
                <p className="property-otp-sms-confirm__property-name">
                  {otpSmsConfigModal.propertyTitle}
                </p>
              ) : null}
              <p className="property-otp-sms-confirm__message">
                {strings.otpSmsConfigProdDevModeMessage.replace(
                  "{{mode}}",
                  otpSmsProdDevModeConfirm.modeLabel ||
                    strings.otpSmsConfigSmsModeOff,
                )}
              </p>
            </div>
            <div className="property-otp-sms-confirm__footer">
              <button
                type="button"
                className="property-otp-sms-modal__btn property-otp-sms-modal__btn--ghost"
                onClick={closeOtpSmsProdDevModeConfirm}
                disabled={otpSmsConfigModal.saving}
              >
                {strings.cancel}
              </button>
              <button
                type="button"
                className="property-otp-sms-modal__btn property-otp-sms-modal__btn--solid"
                onClick={submitOtpSmsConfigSave}
                disabled={otpSmsConfigModal.saving}
              >
                {strings.otpSmsConfigProdDevModeConfirm}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {otpSmsStagingLiveConfirm.open ? (
        <div
          className="property-otp-sms-confirm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="otpSmsStagingLiveTitle"
          onClick={closeOtpSmsStagingLiveConfirm}
        >
          <div
            className="property-otp-sms-confirm__dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="property-otp-sms-confirm__header">
              <h5
                className="property-otp-sms-confirm__title"
                id="otpSmsStagingLiveTitle"
              >
                {strings.otpSmsConfigStagingLiveSmsTitle}
              </h5>
              <button
                type="button"
                className="property-otp-sms-confirm__close"
                aria-label={strings.cancel}
                onClick={closeOtpSmsStagingLiveConfirm}
                disabled={otpSmsConfigModal.saving}
              >
                ×
              </button>
            </div>
            <div className="property-otp-sms-confirm__body">
              {otpSmsConfigModal.propertyTitle ? (
                <p className="property-otp-sms-confirm__property-name">
                  {otpSmsConfigModal.propertyTitle}
                </p>
              ) : null}
              <p className="property-otp-sms-confirm__message">
                {strings.otpSmsConfigStagingLiveSmsMessage}
              </p>
            </div>
            <div className="property-otp-sms-confirm__footer">
              <button
                type="button"
                className="property-otp-sms-modal__btn property-otp-sms-modal__btn--ghost"
                onClick={closeOtpSmsStagingLiveConfirm}
                disabled={otpSmsConfigModal.saving}
              >
                {strings.cancel}
              </button>
              <button
                type="button"
                className="property-otp-sms-modal__btn property-otp-sms-modal__btn--solid"
                onClick={submitOtpSmsConfigSave}
                disabled={otpSmsConfigModal.saving}
              >
                {strings.otpSmsConfigStagingLiveSmsConfirm}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

};

export default Property;
